// packages/web/src/components/onboarding/onboarding-mfa-enroll.tsx
import { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { mfaSetupOptions } from "@/api/me/query-options";
import { useVerifyMfa } from "@/api/me/mutations";

export interface OnboardingMfaEnrollProps {
  onDone: () => void;
}

export function OnboardingMfaEnroll({ onDone }: OnboardingMfaEnrollProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setup = useQuery(mfaSetupOptions());
  const verifyMut = useVerifyMfa();
  const [code, setCode] = useState("");
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  async function onSignOut() {
    await navigate({ to: "/sign-out" });
  }

  async function onVerify() {
    if (code.length !== 6) {
      setVerifyError(t("onboarding.mfa-enroll.errors.code-length"));
      return;
    }
    setVerifyError(null);
    try {
      const result = await verifyMut.mutateAsync({ body: { code } });
      setBackupCodes(result.backupCodes);
    } catch {
      setVerifyError(t("onboarding.mfa-enroll.errors.code-invalid"));
    }
  }

  async function copyCodes() {
    if (!backupCodes) return;
    await navigator.clipboard.writeText(backupCodes.join("\n"));
    toast.success(t("onboarding.mfa-enroll.toast.codes-copied"));
  }

  if (backupCodes) {
    return (
      <div className="mx-auto max-w-md space-y-4 p-6">
        <h1 className="text-xl font-semibold text-ink-900">
          {t("onboarding.mfa-enroll.backup.title")}
        </h1>
        <p className="text-[13px] text-ink-700">{t("onboarding.mfa-enroll.backup.description")}</p>
        <ul className="rounded-3 border border-line bg-surface-1 p-3 font-mono text-[12px] text-ink-900">
          {backupCodes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
          <Button variant="primary" className="w-full sm:w-auto" onClick={copyCodes}>
            {t("onboarding.mfa-enroll.backup.copy.label")}
          </Button>
          <Button variant="default" className="w-full sm:w-auto" onClick={onDone}>
            {t("onboarding.mfa-enroll.backup.continue.label")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md space-y-4 p-6">
      <h1 className="text-xl font-semibold text-ink-900">{t("onboarding.mfa-enroll.title")}</h1>
      <p className="text-[13px] text-ink-700">{t("onboarding.mfa-enroll.description")}</p>
      {setup.data ? (
        <>
          <div className="mx-auto" dangerouslySetInnerHTML={{ __html: setup.data.qrSvg }} />
          <p className="break-all text-[11px] text-ink-500">{setup.data.otpauthUrl}</p>
        </>
      ) : null}
      <div className="space-y-1.5">
        <Label htmlFor="mfa-code">{t("onboarding.mfa-enroll.code.label")}</Label>
        <Input
          id="mfa-code"
          inputMode="numeric"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replaceAll(/[^0-9]/g, ""))}
          aria-invalid={!!verifyError}
        />
        {verifyError ? <p className="text-[12px] text-vroom-red">{verifyError}</p> : null}
      </div>
      <Button
        variant="primary"
        className="w-full"
        onClick={onVerify}
        disabled={verifyMut.isPending}
      >
        {t("onboarding.mfa-enroll.verify.label")}
      </Button>
      <Button variant="default" className="w-full" onClick={() => void onSignOut()}>
        {t("common.nav.sign-out.label")}
      </Button>
    </div>
  );
}
